import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, updateDoc, getDoc, arrayUnion } from "firebase/firestore";
import { firestore } from "../lib/firebaseConfig";
import PageWrapper from '../components/PageWrapper';
import PageTitle from '../components/PageTitle';
import Card from '../components/Card';
import BuildingInfo from '../components/BuildingInfo';
import BuildingImage from '../assets/images/building.png';
import UserContacts from '../components/UserContacts';
import BlockTitle from '../components/BlockTitle';
import DetailsList from '../components/DetailsList';
import TableCustomers from '../components/TableCustomers';
import SaleStatus from '../components/SaleStatus';
import Vindication from '../components/Vindication';
import BuildingIcon from '../assets/images/building.svg';
import WalletIcon from '../assets/images/wallet.svg';
import ShovelIcon from '../assets/images/shovel.svg';
import PaymentsTableHeader from "../components/PaymentsTableHeader";
import Modal from '../components/Modal';
import AddEditClient from "../components/AddEditClient";
import AddEditFlat from "../components/AddEditFlat";
import EditInvestmentModal from '../components/EditInvestmentModal';
import FinishStage from '../components/FinishStage';
import CardSkeleton from '../components/CardSkeleton';
import TableSkeleton from '../components/TableSkeleton';
import { sendEmail } from '../lib/emailService';
import { paymentDueTemplate } from '../lib/emailTemplates';

import { getInvestmentDetails, fixPaymentStatuses } from '../components/PopulateDataButtons';
import UploadContractModal from '../components/UploadContractModal';
import { useInvestment } from '../context/InvestmentContext';
import { useAuth } from '../context/AuthContext';

// Test mode: all emails go to this address
const TEST_EMAIL_RECIPIENT = 'pawel.reszka@inigra.eu';

const Payments = () => {
  const { canEdit, isAdmin } = useAuth();
  const readOnly = !canEdit('/customers');
  const { selectedInvestmentId } = useInvestment();
  const [investment, setInvestment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFlatModalOpen, setIsFlatModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flatStats, setFlatStats] = useState({
    total: 0,
    sold: 0,
    reserved: 0,
    residential: 0,
    service: 0,
  });

  const [isEditInvestmentModalOpen, setIsEditInvestmentModalOpen] = useState(false);
  const [isFinishStageModalOpen, setIsFinishStageModalOpen] = useState(false);
  const [isUploadContractOpen, setIsUploadContractOpen] = useState(false);

const openFinishStageModal = () => {
  setIsFinishStageModalOpen(true);
};


const executeFinishStageModal = async () => {
  const finishedStage = investment.current_stage;
  const investmentId = selectedInvestmentId;
  const today = new Date();
  const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD

  // Calculate payment due date (7 days from today)
  const paymentDueDate = new Date(today);
  paymentDueDate.setDate(paymentDueDate.getDate() + 7);
  const paymentDueDateString = paymentDueDate.toISOString().split('T')[0];

  console.log(`Finishing stage ${finishedStage}...`);
  console.log(`Payment due date set to: ${paymentDueDateString} (7 days from ${todayString})`);

  try {
    // Step 1: Update investment stage with completion history
    const investmentDocRef = doc(firestore, "investments", investmentId);
    await updateDoc(investmentDocRef, {
      current_stage: investment.current_stage + 1,
      completed: investment.current_stage === investment.stages_total,
      stageCompletionHistory: arrayUnion({
        stageNumber: finishedStage,
        completedAt: todayString,
        paymentDueDate: paymentDueDateString
      })
    });
    console.log("Investment stage updated successfully");

    // Step 2: Find all flats and process payments for the finished stage
    const flatsSnapshot = await getDocs(collection(firestore, `investments/${investmentId}/flats`));
    let paymentsProcessed = 0;
    let emailsSent = 0;

    for (const flatDoc of flatsSnapshot.docs) {
      const flatId = flatDoc.id;
      const flatData = flatDoc.data();

      // Only process sold flats
      if (flatData.status !== 'sold') continue;

      // Get payments for this flat
      const paymentsSnapshot = await getDocs(
        collection(firestore, `investments/${investmentId}/flats/${flatId}/payments`)
      );

      // Get customer data for email
      let customerName = 'Szanowny Kliencie';
      if (flatData.clients && flatData.clients.length > 0) {
        try {
          const customerDoc = await getDoc(doc(firestore, `customers/${flatData.clients[0]}`));
          if (customerDoc.exists()) {
            const customerData = customerDoc.data();
            customerName = customerData.buyers?.[0]?.name || customerData.name || 'Szanowny Kliencie';
          }
        } catch (e) {
          console.error('Error fetching customer:', e);
        }
      }

      for (const paymentDoc of paymentsSnapshot.docs) {
        const paymentData = paymentDoc.data();

        // Match on linkedStage (preferred) or fall back to stageNumber for unmigrated data
        const paymentLinkedStage = paymentData.linkedStage ?? paymentData.stageNumber;
        const isDueByStage = paymentData.dueType !== 'fixed_date' && paymentLinkedStage === finishedStage;

        if (isDueByStage) {
          console.log(`Processing payment for flat ${flatData.code}, stage ${finishedStage}, payment #${paymentData.paymentNumber}`);

          // Set payment due date to 7 days from now and activate the payment
          await updateDoc(paymentDoc.ref, {
            paymentDueDate: paymentDueDateString,
            stageCompletedDate: todayString,
            ...(paymentData.payment_status === 'Oczekuje' || paymentData.payment_status === 'Do zapłaty'
              ? { payment_status: 'Do zapłaty' }
              : {}),
          });
          paymentsProcessed++;

          // Send "payment is due" email
          if (paymentData.payment_status !== 'Zapłacono') {
            try {
              const emailTemplate = paymentDueTemplate({
                customerName: customerName,
                apartmentCode: flatData.code,
                dueAmount: paymentData.amount || 0,
                installmentNumber: paymentData.paymentNumber,
                contractNumber: flatData.contract_number || '-',
                stageNumber: finishedStage,
                paymentDueDate: paymentDueDateString
              });

              await sendEmail(
                TEST_EMAIL_RECIPIENT, // Test mode: send to test email
                emailTemplate.subject,
                emailTemplate.html,
                emailTemplate.text,
                [], // No attachments
                {
                  type: 'payment_due',
                  investmentId: investmentId,
                  flatId: flatId,
                  flatCode: flatData.code,
                  customerId: flatData.clients?.[0] || null,
                  customerName: customerName,
                  paymentId: paymentDoc.id,
                  stageNumber: finishedStage
                }
              );
              emailsSent++;
              console.log(`Email sent for flat ${flatData.code}, payment ${paymentData.paymentNumber}`);
            } catch (emailError) {
              console.error(`Error sending email for flat ${flatData.code}:`, emailError);
            }
          }
        }
      }
    }

    console.log(`Stage ${finishedStage} completed. Payments processed: ${paymentsProcessed}, Emails sent: ${emailsSent}`);
    alert(`Etap ${finishedStage} zakończony!\n\nPłatności zaktualizowane: ${paymentsProcessed}\nEmaile wysłane: ${emailsSent}`);

  } catch (error) {
    console.error("Error finishing stage:", error);
    alert("Wystąpił błąd podczas kończenia etapu.");
  }

  // Update local state
  setInvestment((prevInvestment) => ({
    ...prevInvestment,
    current_stage: investment.current_stage + 1,
    completed: investment.current_stage === investment.stages_total,
  }));

  setIsFinishStageModalOpen(false);
};

const closeFinishStageModal = () => {
  setIsFinishStageModalOpen(false);
};

const openEditInvestmentModal = () => {
  setIsEditInvestmentModalOpen(true);
};

const closeEditInvestmentModal = () => {
  setIsEditInvestmentModalOpen(false);
};

  useEffect(() => {
    const fetchInvestmentData = async () => {
      if (!selectedInvestmentId) return;
      setLoading(true);
      try {
        const data = await getInvestmentDetails(selectedInvestmentId);
        if (data) {
          setInvestment(data);
          console.log('All flats:', data.flats);
          console.log('Residential sold flats:', data.flats?.filter(f => f.type?.toLowerCase() === 'residential' && f.status === 'sold'));
          console.log('Commercial sold flats:', data.flats?.filter(f => f.type?.toLowerCase() === 'commercial' && f.status === 'sold'));
          setClients(data.flats);
          setFlatStats({
            total: data.totalFlats,
            sold: data.soldFlats,
            reserved: data.reservedFlats,
            free: data.totalFlats - data.soldFlats,
            residential: data.flatTypeCounts.residential || 0,
            service: data.flatTypeCounts.commercial || 0,
            up_ud: data.flatTypeCounts.up_ud || 0,
            soldSize: data.soldSize,
            totalSize: data.totalSize,
            incomeToCostRatio: data.incomeToCostRatio,
            totalIncome: data.totalIncome,
            totalCost: data.totalCost
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInvestmentData();
  }, [selectedInvestmentId]);

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const openModal = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

 const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  const openFlatModal = () => {
    console.log("openFlatModal");
    setIsFlatModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

 const closeFlatModal = () => {
    setIsFlatModalOpen(false);
    document.body.style.overflow = 'auto';
    const fetchInvestmentData = async () => {
      const data = await getInvestmentDetails(selectedInvestmentId);
      if (data) {
        setInvestment(data);
        console.log(data.flats);
        setClients(data.flats);
        setFlatStats({
          total: data.totalFlats,
          sold: data.soldFlats,
          reserved: data.reservedFlats,
          residential: data.flatTypeCounts.residential || 0,
          service: data.flatTypeCounts.commercial || 0,
        });
      }
    };
    fetchInvestmentData();
  };

  const handleSave = () => {
    setIsModalOpen(false);
    setSelectedClient(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <PageWrapper>
      <PageTitle>Inwestycja - Płatności - Szczegóły</PageTitle>

      <div className="mt-[20px]">
        {loading ? (
          <CardSkeleton />
        ) : (
        <Card className="pt-[20px]">
          {investment && (
            <>

            { investment.completed ? (
              <BuildingInfo
                title={investment.name}
                type="Inwestycja"
                imagePath={BuildingImage}
                action1_title="Dodaj lokal"
                action1_function={openFlatModal}
                action2_title="Edytuj inwestycję"
                action2_function={openEditInvestmentModal}
                action3_title={'Zakończ etap ' + investment.current_stage + ' z ' + investment.stages_total }
                action3_function={openFinishStageModal}
                both_used='tak'
                button3_used='nie'
                button4_used='nie'
              /> )
            : (
              <BuildingInfo
                title={investment.name}
                type="Inwestycja"
                imagePath={BuildingImage}
                action1_title="Dodaj lokal"
                action1_function={openFlatModal}
                action2_title="Edytuj inwestycję"
                action2_function={openEditInvestmentModal}
                action3_title={'Zakończ etap ' + investment.current_stage + ' z ' + investment.stages_total }
                action3_function={openFinishStageModal}
                both_used='tak'
                button3_used='tak'
                button4_used='nie'
              />

                        )
          }
              {!readOnly && (
                <div className="mt-[12px]">
                  <button
                    onClick={() => setIsUploadContractOpen(true)}
                    className="text-sm text-[var(--maroon)] border border-[var(--maroon)] rounded-[6px] px-4 py-2 hover:bg-[var(--maroon)] hover:text-white transition"
                  >
                    Dodaj umowe (PDF)
                  </button>
                </div>
              )}
              <UserContacts
                className="mt-[20px]"
                address={investment.location}
                userName={investment.manager}
                phoneNumber={investment.phone}
              />
              <DetailsList
                className="mt-[22px]"
                items={[
                  { title: 'Liczba Lokali', value: flatStats.total },
                  { title: 'Lokale Mieszkanowe', value: flatStats.residential },
                  { title: 'Lokale Usługowe', value: flatStats.service },
                ]}
              />
              <div className="mt-[26px]">
                <BlockTitle>Status sprzedaży :</BlockTitle>
                <div className="flex flex-wrap mt-[20px] gap-2.5">
                  <SaleStatus
                    icon={BuildingIcon}
                    title="Liczba lokali"
                    amount={flatStats.total}
                    maxAmount={flatStats.total}
                    fillColor="#47AD94D9"
                    displayAmount={flatStats.sold + " / " + flatStats.total + " (" + (flatStats.sold / flatStats.total * 100).toFixed(0) + " %)"}
                  />
                  <SaleStatus
                    icon={ShovelIcon}
                    title="Sprzedana powierzchnia"
                    amount={flatStats.soldSize}
                    maxAmount={flatStats.totalSize}
                    fillColor="#E19E0D"
                    displayAmount={flatStats.soldSize?.toFixed(2) + " / " + flatStats.totalSize?.toFixed(2) + " m² (" + (flatStats.soldSize / flatStats.totalSize * 100).toFixed(0) + " %)"}
                  />                   
                  <SaleStatus
                    icon={WalletIcon}
                    title="Sprzedane / Umowa z GW"
                    amount={flatStats.incomeToCostRatio ? flatStats.incomeToCostRatio * 100 : 0}
                    maxAmount={100}
                    fillColor="#E1360DD9"
                    displayAmount={
                      ((flatStats.totalIncome || 0) / 1000000).toFixed(2) + " mln / " +
                      ((flatStats.totalCost || 0) / 1000000).toFixed(2) + " mln (" +
                      (flatStats.incomeToCostRatio ? (flatStats.incomeToCostRatio * 100).toFixed(1) : 0) + "%)"
                    }
                  />
               {/*   <SaleStatus
                    icon={ReservationIcon}
                    title="Wolne"
                    amount={flatStats.free}
                    maxAmount={flatStats.total}
                    fillColor="#027EF4D9"
                    displayAmount={flatStats.free}
                  /> */}
                 
                </div>
              </div>
            </>
          )}
        </Card>
        )}

        {/* <Vindication /> */}
      </div>

{loading ? (
  <>
    <TableSkeleton className="mt-[28px]" rows={5} columns={7} />
    <TableSkeleton className="mt-[28px]" rows={3} columns={7} />
  </>
) : (
  <>
<TableCustomers
  className="mt-[28px]"
  topContent={<PaymentsTableHeader onModalOpen={openModal} headerTitle="Lokale Mieszkaniowe [VAT 8]"/>}
  data={clients.filter(flat => flat.type?.toLowerCase() === "residential" && flat.status === "sold").map(flat => ({
    investmentId: flat.investmentId,
    flatId: flat.id,
    payment_status: flat.payment_status,
    clientIds: flat.clients ? flat.clients.map(client => client.id) : [],  // Fallback to empty array
    type: flat.type,
    code: flat.code || '-',
    'Nabywca': flat.clients && flat.clients.length > 0
      ? flat.clients.map(c => c.name)
      : '-',  // Show default text for unsold flats
    an_amount: flat.an_amount || 0,  
    mandatory_amount: flat.mandatory_amount ||  0,
    paid_amount: flat.paid_amount || 0,
    missing_amount: flat.missing_amount ||  0



  }))}
  setData={setClients}
  showTotalSecondary={false}
  hasActions
  onEditClient={handleEditClient}
  headerTitle="Lokale Mieszkaniowe [VAT 8]"
/>

<TableCustomers
  className="mt-[28px]"
  topContent={<PaymentsTableHeader onModalOpen={openModal} headerTitle="Lokale Usługowe [VAT 23]" />}
  data={clients.filter(flat => flat.type?.toLowerCase() === "commercial" && flat.status === "sold").map(flat => ({
    investmentId: flat.investmentId,
    flatId: flat.id,
    payment_status: flat.payment_status,
    clientIds: flat.clients ? flat.clients.map(client => client.id) : [],  // Fallback to empty array
    type: flat.type,
    code: flat.code || '-',
    'Nabywca': flat.clients && flat.clients.length > 0
      ? flat.clients.map(c => c.name)
      : '-',  // Show default text for unsold flats
    an_amount: flat.an_amount || 0,  
    mandatory_amount: flat.mandatory_amount ||  0,
    paid_amount: flat.paid_amount || 0,
    missing_amount: flat.missing_amount ||  0
        

        
        



  }))}
  setData={setClients}
  showTotalSecondary={false}
  hasActions
  onEditClient={handleEditClient}
  headerTitle="Lokale Usługowe [VAT 23]"
/>
  </>
)}

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <AddEditClient client={selectedClient} onSave={handleSave} onCancel={closeModal} investmentId={selectedInvestmentId} investmentTotalStages={investment?.stages_total} investmentStage={investment?.current_stage}/>
      </Modal>

      <Modal isOpen={isFlatModalOpen} onClose={closeFlatModal}>
        <AddEditFlat onSave={closeFlatModal} onCancel={closeFlatModal} investmentId={selectedInvestmentId}/>
      </Modal>

    <EditInvestmentModal
      isOpen={isEditInvestmentModalOpen}
      onClose={closeEditInvestmentModal}
      investmentId={selectedInvestmentId}
      onSave={async () => {
        const data = await getInvestmentDetails(selectedInvestmentId);
        if (data) {
          setInvestment(data);
          setFlatStats({
            total: data.totalFlats,
            sold: data.soldFlats,
            reserved: data.reservedFlats,
            free: data.totalFlats - data.soldFlats,
            residential: data.flatTypeCounts.residential || 0,
            service: data.flatTypeCounts.commercial || 0,
            up_ud: data.flatTypeCounts.up_ud || 0,
            soldSize: data.soldSize,
            totalSize: data.totalSize,
            incomeToCostRatio: data.incomeToCostRatio,
            totalIncome: data.totalIncome,
            totalCost: data.totalCost
          });
        }
      }}
    />

      <Modal isOpen={isFinishStageModalOpen} onClose={closeFinishStageModal}>
        <FinishStage onSave={executeFinishStageModal} onCancel={closeFinishStageModal} investmentId={selectedInvestmentId} investmentStage={investment?.current_stage}/>
      </Modal>

      <UploadContractModal
        isOpen={isUploadContractOpen}
        onClose={() => setIsUploadContractOpen(false)}
        investmentId={selectedInvestmentId}
        onSave={async () => {
          const data = await getInvestmentDetails(selectedInvestmentId);
          if (data) {
            setInvestment(data);
            setClients(data.flats);
            setFlatStats({
              total: data.totalFlats,
              sold: data.soldFlats,
              reserved: data.reservedFlats,
              free: data.totalFlats - data.soldFlats,
              residential: data.flatTypeCounts.residential || 0,
              service: data.flatTypeCounts.commercial || 0,
              up_ud: data.flatTypeCounts.up_ud || 0,
              soldSize: data.soldSize,
              totalSize: data.totalSize,
              incomeToCostRatio: data.incomeToCostRatio,
              totalIncome: data.totalIncome,
              totalCost: data.totalCost
            });
          }
        }}
      />

    </PageWrapper>
  );
};

export default Payments;
